// ── VENDOR PAYMENTS ───────────────────────────────────────────────────────────
// Bookkeeper command center for everything vendor-side. Five views:
//
//   Inbox       — vendor receipts uploaded but not yet approved
//   Due         — POs where the client deposit landed but the vendor hasn't been paid
//   Outstanding — POs where the vendor deposit was paid but the final still isn't
//   History     — every paid record, the audit trail
//   PO drill-down (inline) — click any row to see PO + items + payments + receipts
//
// The data layer is wired up by Pass 1 (AppContext.applyVendorReceiptSideEffects):
// approving a vendor receipt writes vendor_payments + sets orderPlacedDate + advances
// item status. Manual "+ Record Payment" runs the same cascade so the two paths stay
// in sync.
//
// Aesthetic: white background, hairline (#E0E0DC) borders, pill buttons, color
// reserved for status (yellow/fuchsia/green dots).
//
// PAID / OUTSTANDING (2026-09-20 realignment): every list decision and the
// drill-down header read `isPoFullyPaid` (utils.js) — the same predicate
// Reconciliation and the Invoices tab use. That means: payments match by
// po_id (not by a reference-string search), EVERY payment_type counts
// (a 'Fee' row for freight/crating/tariff closes the freight the PO
// carries), refunds/writeoffs subtract, cancelled items leave the
// expected total, and a $0.50 / 0.5% tolerance band closes the last
// cents. Before this the tab kept a 2026-06 copy of the rule (principal
// types only, half-cent tolerance, reference match) and showed POs as
// Outstanding that every other page called paid — Cimon, 2026-09-20:
// "a lot of overdue payments that have actually been made." The word
// OVERDUE was also the ship estimate passing, not a payment being late;
// it now reads as a muted "ship est. passed".

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAppContext } from './AppContext';
import { calcVendorCreditBalance, poSubtotalLive, fmtMoney, poPaidReconciliation, findOrphanReceiptPayments, buildOwnerAdjustmentRow, estimatedShipDate, displayNameFor, allocateVendorPaymentToItems, allocateVendorPaymentEvenly, calcVendorDepositWithFees, formatVendorPaymentReference, paymentsForPO, poTotalPaid, isPoFullyPaid } from '../utils';
import CancelItemModal from './CancelItemModal';
import ConfirmationModal from './ConfirmationModal';
import { CurrencyInput } from './ui';

// ── FORMAT ────────────────────────────────────────────────────────────────────
// Both fmtMoney and fmtMoneyFull are aliased to the canonical fmtMoney
// (utils.js). Earlier this file had a rounded compact variant but the
// firm's standard is $X.XX everywhere — see feedback memory.
var fmtMoneyFull = fmtMoney;
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    var s = String(iso);
    var m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    var d = m ? new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3])) : new Date(s);
    if (isNaN(d.getTime())) return s.slice(0, 10);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) { return String(iso).slice(0, 10); }
}
function leadWeeksOf(item) {
  if (!item) return 0;
  if (item.leadWeeks) return parseInt(item.leadWeeks) || 0;
  var lt = String(item.leadTime || '').toLowerCase();
  var m = lt.match(/(\d+)\s*-?\s*(\d+)?/);
  if (!m) return 0;
  if (m[2]) return parseInt(m[2]);
  return parseInt(m[1]);
}

// ── STYLES ────────────────────────────────────────────────────────────────────
var S = {
  wrap:        { fontFamily: "'DM Sans', sans-serif", color: '#111111', background: '#FFFFFF' },
  toolbar:     { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' },
  pageTitle:   { fontSize: 20, fontWeight: 600, color: '#111' },
  subTitle:    { fontSize: 12, color: '#888' },
  pillRow:     { display: 'flex', gap: 6, flexWrap: 'wrap' },
  pill:        function(active) {
    return {
      height: 30, padding: '0 14px', borderRadius: 18,
      border: active ? '1px solid #111' : '0.5px solid #E0E0DC',
      background: active ? '#111' : '#FFFFFF',
      color: active ? '#FFFFFF' : '#444',
      fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
      cursor: 'pointer', whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center', gap: 6,
    };
  },
  countBadge:  function(active) {
    return {
      fontSize: 10, fontWeight: 600,
      padding: '1px 6px', borderRadius: 8,
      background: active ? 'rgba(255,255,255,0.2)' : '#F0F0EC',
      color: active ? '#fff' : '#666',
    };
  },
  primaryBtn:  { height: 32, padding: '0 16px', borderRadius: 18, border: 'none', background: '#111', color: '#FFFFFF', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' },
  ghostBtn:    { height: 30, padding: '0 14px', borderRadius: 18, border: '0.5px solid #E0E0DC', background: '#FFFFFF', color: '#111', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' },
  successBtn:  { height: 28, padding: '0 14px', borderRadius: 16, border: 'none', background: '#1A5C38', color: '#FFFFFF', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' },
  card:        { background: '#FFFFFF', border: '0.5px solid #E0E0DC', borderRadius: 10, overflow: 'hidden' },
  th:          { padding: '11px 14px', textAlign: 'left', fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888', whiteSpace: 'nowrap', borderBottom: '0.5px solid #E0E0DC', background: '#FAFAF8' },
  td:          { padding: '12px 14px', fontSize: 12, color: '#444', borderBottom: '0.5px solid #EFEFEC', whiteSpace: 'nowrap' },
  emptyState:  { padding: '60px 18px', textAlign: 'center', color: '#888', fontSize: 13, fontStyle: 'italic' },
  row:         function(clickable) { return { cursor: clickable ? 'pointer' : 'default', background: '#fff', transition: 'background 0.1s' }; },
  dot:         function(c) { return { display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: c, marginRight: 6, verticalAlign: 'middle' }; },
};

// ── DATA HELPERS ──────────────────────────────────────────────────────────────
// paymentsForPO + poTotalPaid lifted to utils.js (2026-07-13, Thread Phase 1)
// so the thread derivation layer and this section share one formula.
// Imported above; these comments mark the prior local-copy locations.

function vendorPaymentStatus(po, vendorPayments) {
  var matches = paymentsForPO(po, vendorPayments);
  var depTypes = ['Deposit', 'Deposit Payment', 'Partial Payment'];
  var finalTypes = ['Final', 'Final Payment', 'Full', 'Adjustment'];
  var hasDeposit = matches.some(function(p) { return depTypes.indexOf(p.payment_type) >= 0; });
  var hasFinal   = matches.some(function(p) { return finalTypes.indexOf(p.payment_type) >= 0; });
  if (hasFinal)   return 'final-paid';
  if (hasDeposit) return 'deposit-paid';
  return 'no-vendor-payment';
}
function clientDepositReceivedForPO(po, items, invoices) {
  // Client deposit received = there's a deposit invoice marked Paid for items on this PO.
  // Falls back to checking item.status >= Deposit Paid if invoice link missing.
  var poItemIds = po && Array.isArray(po.items) ? po.items : [];
  if (poItemIds.length === 0) return false;

  // First-pass: explicit invoice match
  var depositInvoices = (invoices || []).filter(function(inv) {
    var t = inv.invoiceType || '';
    var st = (inv.status || '').toLowerCase();
    return t === 'deposit' && (st === 'paid' || st === 'partially paid');
  });
  var paidByInvoice = depositInvoices.some(function(inv) {
    var ids = inv.itemIds || inv.item_ids || [];
    return ids.some(function(id) { return poItemIds.indexOf(id) >= 0; });
  });
  if (paidByInvoice) return true;

  // Fallback: any item on the PO at Deposit Paid status or beyond
  var paidStatuses = ['Deposit Paid', 'PO Issued', 'Ordered', 'Shipped', 'At Receiver', 'Delivered', 'Installed', 'Complete'];
  return (items || []).some(function(it) {
    return poItemIds.indexOf(it.id) >= 0 && paidStatuses.indexOf(it.status) >= 0;
  });
}

// Dollar-based PO status — mirrors the drilldown header's reconciliation
// logic (line ~660: poPaidReconciliation). Replaces the string-based
// vendorPaymentStatus() for sub-tab membership decisions so the list
// filters and the drilldown header agree on "fully paid."
//
// Tolerance: half a cent. The reconciliation helper uses its own band
// (PO_PAYMENT_TOLERANCE_*), but for membership purposes "paid vs unpaid"
// only cares about whether there's a real remaining balance.
//
// Why this exists separately from vendorPaymentStatus: a 'Deposit'
// payment_type that happens to cover 100% of the PO (e.g. Armadillo
// $410 on a $410 PO, classified vendor_deposit because the vendor
// invoiced it as a "deposit") was incorrectly landing in Outstanding
// under the string-based filter — paid in full but tagged as
// deposit-only. Dollar-based filter correctly excludes it.
//
// 2026-09-20: now a thin reading of the canonical isPoFullyPaid so this
// tab can never disagree with Reconciliation / Invoices about "paid".
// poPaidPicture is the shared shape; poDollarStatus keeps its three
// words for the list filters.
function poPaidPicture(po, vendorPayments, items, vendorCreditLedger) {
  var canon = isPoFullyPaid(po, items, vendorPayments, [], vendorCreditLedger || []);
  var poId = String(po && po.id || '');
  // Paid by po_id — the canonical link. Legacy rows with no po_id fall
  // back to the reference search so nothing recorded goes invisible.
  var byId = (vendorPayments || []).filter(function(vp) { return vp && String(vp.po_id || vp.poId || '') === poId; });
  var pool = byId.length ? byId : paymentsForPO(po, vendorPayments);
  var paidAll = pool.reduce(function(s, vp) { return s + (parseFloat(vp.amount) || 0); }, 0);
  var paid = canon.expectedTotal > 0 ? canon.paidTotal : Math.round(paidAll * 100) / 100;
  var expected = canon.expectedTotal > 0 ? canon.expectedTotal : poSubtotalLive(po, items);
  var remaining = Math.max(0, Math.round((expected - paid) * 100) / 100);
  var fullyPaid = canon.isFullyPaid || (paid >= 0.005 && remaining <= canon.tolerance);
  return { paid: paid, expected: expected, remaining: fullyPaid ? 0 : remaining, fullyPaid: fullyPaid, tolerance: canon.tolerance };
}

function poDollarStatus(po, vendorPayments, items, vendorCreditLedger) {
  var pic = poPaidPicture(po, vendorPayments, items, vendorCreditLedger);
  if (pic.paid < 0.005) return 'unpaid';
  if (pic.fullyPaid) return 'fully-paid';
  return 'partially-paid';
}

// Per-phase expected vs paid breakdown for the drilldown header.
// Computes:
//   expectedDeposit  — sum over items of calcVendorDepositWithFees
//                      (at-cost, per-item exact: 50% of product when
//                      vdp<100; product + all fees when vdp=100)
//   expectedFinal    — poSubtotalLive − expectedDeposit (remainder)
//   paidDeposit      — sum of payments tagged Deposit/Partial Payment
//   paidFinal        — sum of payments tagged Final/Final Payment/Full/
//                      Adjustment
//   gap*             — paid − expected for each phase + total
//
// Cancelled items are excluded from expected* calculations so the
// numbers reflect the current commitment, not what was originally
// quoted.
function poFinancialPicture(po, vendorPayments, items) {
  var poItemIds = (po && po.items) || [];
  var poItems = (items || []).filter(function(it) {
    return poItemIds.indexOf(it.id) >= 0 && it.status !== 'Cancelled';
  });

  var expectedDeposit = poItems.reduce(function(sum, it) {
    var net = parseFloat(it.netPrice) || 0;
    var qty = parseFloat(it.qty)      || 1;
    var vdp = parseFloat(it.depositPct) || 50;
    return sum + calcVendorDepositWithFees(net, qty, vdp, it.fees || {});
  }, 0);
  var poTotal = poSubtotalLive(po, items);
  var expectedFinal = Math.max(0, poTotal - expectedDeposit);

  var matches = paymentsForPO(po, vendorPayments);
  var depTypes   = ['Deposit', 'Deposit Payment', 'Partial Payment'];
  var finalTypes = ['Final', 'Final Payment', 'Full', 'Adjustment'];
  var paidDeposit = matches
    .filter(function(p) { return depTypes.indexOf(p.payment_type) >= 0; })
    .reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  var paidFinal = matches
    .filter(function(p) { return finalTypes.indexOf(p.payment_type) >= 0; })
    .reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  // Everything else — 'Fee' rows (freight / crating / tariff paid on a
  // separate vendor bill) and any untagged row. They are real money
  // against a PO total that already carries those fee categories, so
  // they count toward paid (2026-09-20; matches isPoFullyPaid).
  var paidFees = matches
    .filter(function(p) { return depTypes.indexOf(p.payment_type) < 0 && finalTypes.indexOf(p.payment_type) < 0; })
    .reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  var paidTotal = paidDeposit + paidFinal + paidFees;

  return {
    expectedDeposit: Math.round(expectedDeposit * 100) / 100,
    paidDeposit:     Math.round(paidDeposit     * 100) / 100,
    gapDeposit:      Math.round((paidDeposit - expectedDeposit) * 100) / 100,
    expectedFinal:   Math.round(expectedFinal   * 100) / 100,
    paidFinal:       Math.round(paidFinal       * 100) / 100,
    gapFinal:        Math.round((paidFinal - expectedFinal) * 100) / 100,
    paidFees:        Math.round(paidFees        * 100) / 100,
    poTotal:         Math.round(poTotal         * 100) / 100,
    paidTotal:       Math.round(paidTotal       * 100) / 100,
    gapTotal:        Math.round((paidTotal - poTotal) * 100) / 100,
  };
}

// poPaidReconciliation moved to utils.js so it can be unit-tested without
// pulling in this component's React tree. Imported above.
function poEstShipDate(po, items) {
  // Earliest orderPlacedDate + max leadWeeks among items on this PO.
  // PO-level worst-case projection — slowest item on the critical path,
  // starting from the earliest order date. Routes through the
  // canonical estimatedShipDate helper (utils.js) so the math matches
  // every other consumer that projects forward from orderPlacedDate.
  var poItemIds = po && Array.isArray(po.items) ? po.items : [];
  var poItems = (items || []).filter(function(it) { return poItemIds.indexOf(it.id) >= 0; });
  if (poItems.length === 0) return null;
  var orderDate = poItems
    .map(function(it) { return it.orderPlacedDate; })
    .filter(Boolean)
    .sort()[0];
  if (!orderDate) return null;
  var maxWeeks = Math.max.apply(null, poItems.map(leadWeeksOf).concat([0]));
  return estimatedShipDate({ orderPlacedDate: orderDate, leadWeeks: maxWeeks });
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function VendorPaymentsSection() {
  var ctx = useAppContext();
  var receipts = ctx.receipts || [];
  var purchaseOrders = ctx.purchaseOrders || [];
  var vendorPayments = ctx.vendorPayments || [];
  var items = ctx.items || [];
  var projects = ctx.projects || [];
  var invoices = ctx.invoices || [];
  var vendorCreditLedger = ctx.vendorCreditLedger || [];
  var saveReceipt = ctx.saveReceipt;
  var saveReceiptToDb = ctx.saveReceiptToDb;
  var setVendorPayments = ctx.setVendorPayments;
  var applyVendorReceiptSideEffects = ctx.applyVendorReceiptSideEffects;
  var userEmail = (ctx.userEmail || '').toLowerCase();

  var sts = useState('inbox');         var subTab = sts[0];        var setSubTab = sts[1];
  var pds = useState(null);            var drilldownPOId = pds[0]; var setDrilldownPOId = pds[1];
  var rps = useState(false);           var recordOpen = rps[0];    var setRecordOpen = rps[1];

  // ── Counts for the pill badges ─────────────────────────────────────────────
  var counts = useMemo(function() {
    var inbox = receipts.filter(function(r) {
      var k = r.kind || '';
      var st = r.status || '';
      return (k === 'vendor_deposit' || k === 'vendor_final' || k === 'vendor_full_payment' || k === 'vendor_fee')
          && (st === 'pending' || st === 'needs_link');
    }).length;
    // Due: client deposit landed AND no principal vendor payment yet.
    // Dollar-based: ignores payment_type string, checks paid === 0.
    var due = purchaseOrders.filter(function(po) {
      return clientDepositReceivedForPO(po, items, invoices)
          && poDollarStatus(po, vendorPayments, items, vendorCreditLedger) === 'unpaid';
    }).length;
    // Outstanding: partially paid (paid > 0 AND remaining > 0). A
    // 100%-covering 'Deposit' payment now correctly drops out (full
    // PO total covered → 'fully-paid' → not partially-paid).
    var outstanding = purchaseOrders.filter(function(po) {
      return poDollarStatus(po, vendorPayments, items, vendorCreditLedger) === 'partially-paid';
    }).length;
    var history = vendorPayments.length;
    return { inbox: inbox, due: due, outstanding: outstanding, history: history };
  }, [receipts, purchaseOrders, vendorPayments, items, invoices, vendorCreditLedger]);

  // ── Drill-down view ────────────────────────────────────────────────────────
  if (drilldownPOId) {
    var drillPO = purchaseOrders.find(function(p) { return p.id === drilldownPOId; });
    if (!drillPO) {
      return (<div style={S.wrap}>
        <button style={S.ghostBtn} onClick={function() { setDrilldownPOId(null); }}>← Back</button>
        <div style={{ marginTop: 20, color: '#888' }}>PO not found.</div>
      </div>);
    }
    return <PODrilldown po={drillPO} onBack={function() { setDrilldownPOId(null); }} onRecord={function() { setRecordOpen(true); }} />;
  }

  return (
    <div style={S.wrap}>

      {/* Header */}
      <div style={S.toolbar}>
        <div>
          <div style={S.pageTitle}>Vendor Payments</div>
          <div style={S.subTitle}>Track every payment from inbox to history. Click any PO for the full picture.</div>
        </div>
        <div style={{ flex: 1 }} />
        <button style={S.primaryBtn} onClick={function() { setRecordOpen(true); }} title="For payments without a receipt — receipts auto-create payment rows on approval.">+ Record manually</button>
      </div>

      {/* Sub-tab pills */}
      <div style={Object.assign({}, S.pillRow, { marginBottom: 18 })}>
        <Pill label="Inbox"       count={counts.inbox}       active={subTab === 'inbox'}       onClick={function() { setSubTab('inbox'); }} />
        <Pill label="Due"         count={counts.due}         active={subTab === 'due'}         onClick={function() { setSubTab('due'); }} />
        <Pill label="Outstanding" count={counts.outstanding} active={subTab === 'outstanding'} onClick={function() { setSubTab('outstanding'); }} />
        <Pill label="History"     count={counts.history}     active={subTab === 'history'}     onClick={function() { setSubTab('history'); }} />
      </div>

      {subTab === 'inbox'       && <InboxView      onApplyCascade={applyVendorReceiptSideEffects} onOpenPO={function(id) { setDrilldownPOId(id); }} />}
      {subTab === 'due'         && <DueView        onOpenPO={function(id) { setDrilldownPOId(id); }} onPay={function(po) { setDrilldownPOId(po.id); setRecordOpen(true); }} />}
      {subTab === 'outstanding' && <OutstandingView onOpenPO={function(id) { setDrilldownPOId(id); }} />}
      {subTab === 'history'     && <HistoryView    onOpenPO={function(id) { setDrilldownPOId(id); }} />}

      {/* Record Payment modal */}
      {recordOpen && <RecordPaymentModal po={drilldownPOId ? purchaseOrders.find(function(p) { return p.id === drilldownPOId; }) : null} onClose={function() { setRecordOpen(false); }} />}
    </div>
  );
}

// ── PILL BUTTON ───────────────────────────────────────────────────────────────
function Pill(p) {
  return (
    <button style={S.pill(p.active)} onClick={p.onClick}>
      <span>{p.label}</span>
      {p.count > 0 && <span style={S.countBadge(p.active)}>{p.count}</span>}
    </button>
  );
}

// ── INBOX VIEW ────────────────────────────────────────────────────────────────
function InboxView(p) {
  var ctx = useAppContext();
  var receipts = ctx.receipts || [];
  var purchaseOrders = ctx.purchaseOrders || [];
  var saveReceipt = ctx.saveReceipt;
  var userEmail = (ctx.userEmail || '').toLowerCase();

  var inbox = receipts.filter(function(r) {
    var k = r.kind || '';
    var st = r.status || '';
    return (k === 'vendor_deposit' || k === 'vendor_final' || k === 'vendor_fee')
        && (st === 'pending' || st === 'needs_link');
  });

  async function approveOne(r) {
    if (!r.poId) {
      alert('Receipt needs a PO link before it can be approved. Open the receipt detail in the Receipts tab to set the PO.');
      return;
    }
    var approved = Object.assign({}, r, { status: 'approved', approvedAt: new Date().toISOString(), approvedBy: userEmail });
    saveReceipt(approved);
    if (p.onApplyCascade) await p.onApplyCascade(approved);
  }

  if (inbox.length === 0) return (
    <div style={Object.assign({}, S.card)}>
      <div style={S.emptyState}>No vendor receipts in the inbox. New uploads will land here.</div>
    </div>
  );

  return (
    <div style={S.card}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={S.th}>Vendor</th>
            <th style={S.th}>Kind</th>
            <th style={Object.assign({}, S.th, { textAlign: 'right' })}>Amount</th>
            <th style={S.th}>Date</th>
            <th style={S.th}>Suggested PO</th>
            <th style={S.th}>Confidence</th>
            <th style={Object.assign({}, S.th, { textAlign: 'right' })}>Action</th>
          </tr>
        </thead>
        <tbody>
          {inbox.map(function(r) {
            var po = purchaseOrders.find(function(x) { return x.id === r.poId; });
            var conf = Math.round((parseFloat(r.matchConfidence) || 0) * 100);
            var kindLabel = r.kind === 'vendor_deposit' ? 'Deposit' : r.kind === 'vendor_final' ? 'Final' : 'Fee';
            return (
              <tr key={r.id} style={S.row(true)}
                onClick={function() { if (po) p.onOpenPO(po.id); }}
                onMouseEnter={function(e) { e.currentTarget.style.background = '#FAFAF8'; }}
                onMouseLeave={function(e) { e.currentTarget.style.background = '#FFFFFF'; }}>
                <td style={Object.assign({}, S.td, { fontWeight: 500, color: '#111' })}>{r.vendorName || '—'}</td>
                <td style={S.td}>{kindLabel}</td>
                <td style={Object.assign({}, S.td, { textAlign: 'right', fontWeight: 500, color: '#111' })}>{fmtMoneyFull(r.totalAmount)}</td>
                <td style={S.td}>{fmtDate(r.receiptDate)}</td>
                <td style={Object.assign({}, S.td, { fontFamily: 'monospace' })}>
                  {po ? po.po_ref : <span style={{ color: '#C8322A' }}>— needs link —</span>}
                </td>
                <td style={S.td}>
                  <span style={S.dot(conf >= 80 ? '#1A5C38' : conf >= 50 ? '#F5C800' : '#C8322A')} />
                  {conf}%
                </td>
                <td style={Object.assign({}, S.td, { textAlign: 'right' })}>
                  <button style={S.successBtn} onClick={function(e) { e.stopPropagation(); approveOne(r); }}>Approve</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── DUE VIEW ──────────────────────────────────────────────────────────────────
function DueView(p) {
  var ctx = useAppContext();
  var purchaseOrders = ctx.purchaseOrders || [];
  var vendorPayments = ctx.vendorPayments || [];
  var items = ctx.items || [];
  var invoices = ctx.invoices || [];
  var vendors = ctx.vendors || [];
  var vendorCreditLedger = ctx.vendorCreditLedger || [];

  // Due = client deposit landed AND no vendor payment of any kind yet.
  // Dollar-based via poDollarStatus — matches drilldown reconciliation.
  var due = purchaseOrders.filter(function(po) {
    return clientDepositReceivedForPO(po, items, invoices)
        && poDollarStatus(po, vendorPayments, items, vendorCreditLedger) === 'unpaid';
  });

  if (due.length === 0) return (
    <div style={S.card}>
      <div style={S.emptyState}>No vendor payments due. POs will appear here when the client deposit lands.</div>
    </div>
  );

  return (
    <div style={S.card}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={S.th}>PO</th>
            <th style={S.th}>Vendor</th>
            <th style={S.th}>Project</th>
            <th style={Object.assign({}, S.th, { textAlign: 'right' })}>PO Total</th>
            <th style={Object.assign({}, S.th, { textAlign: 'right' })}>Suggested Deposit</th>
            <th style={Object.assign({}, S.th, { textAlign: 'right' })}>Action</th>
          </tr>
        </thead>
        <tbody>
          {due.map(function(po) {
            var poItemIds = po.items || [];
            var poItems = items.filter(function(it) { return poItemIds.indexOf(it.id) >= 0; });
            var avgVdp = poItems.length > 0
              ? poItems.reduce(function(s, it) { return s + (parseFloat(it.depositPct) || 50); }, 0) / poItems.length
              : 50;
            // Live PO subtotal (product + applicable fees including
            // per-item installation when fees.installation > 0) —
            // matches the PO PDF "Subtotal" the vendor sees and the
            // wire amount the firm should send. The stored po.total_net
            // column goes stale on item edits, so live computation is
            // the source of truth.
            var poTotal   = poSubtotalLive(po, items);
            var suggested = poTotal * avgVdp / 100;
            return (
              <tr key={po.id} style={S.row(true)}
                onClick={function() { p.onOpenPO(po.id); }}
                onMouseEnter={function(e) { e.currentTarget.style.background = '#FAFAF8'; }}
                onMouseLeave={function(e) { e.currentTarget.style.background = '#FFFFFF'; }}>
                <td style={Object.assign({}, S.td, { fontFamily: 'monospace', fontWeight: 500, color: '#111' })}>{po.po_ref || '—'}</td>
                <td style={S.td}>{po.vendorName || '—'}</td>
                <td style={S.td}>{po.project || '—'}</td>
                <td style={Object.assign({}, S.td, { textAlign: 'right', fontWeight: 500, color: '#111' })}>{fmtMoney(poTotal)}</td>
                <td style={Object.assign({}, S.td, { textAlign: 'right', color: '#888' })}>{fmtMoney(suggested)}</td>
                <td style={Object.assign({}, S.td, { textAlign: 'right' })}>
                  <button style={S.successBtn} onClick={function(e) { e.stopPropagation(); p.onPay(po); }}>Pay</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── OUTSTANDING VIEW ──────────────────────────────────────────────────────────
function OutstandingView(p) {
  var ctx = useAppContext();
  var purchaseOrders = ctx.purchaseOrders || [];
  var vendorPayments = ctx.vendorPayments || [];
  var items = ctx.items || [];
  var vendors = ctx.vendors || [];
  var vendorCreditLedger = ctx.vendorCreditLedger || [];

  // Outstanding = partially paid (paid > 0 AND remaining beyond the
  // tolerance band), by the canonical isPoFullyPaid reading.
  var outstanding = purchaseOrders.filter(function(po) {
    return poDollarStatus(po, vendorPayments, items, vendorCreditLedger) === 'partially-paid';
  });

  if (outstanding.length === 0) return (
    <div style={S.card}>
      <div style={S.emptyState}>No outstanding POs. POs with deposit paid but final still owed will appear here.</div>
    </div>
  );

  return (
    <div style={S.card}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={S.th}>PO</th>
            <th style={S.th}>Vendor</th>
            <th style={Object.assign({}, S.th, { textAlign: 'right' })}>PO Total</th>
            <th style={Object.assign({}, S.th, { textAlign: 'right' })}>Expected Dep.</th>
            <th style={Object.assign({}, S.th, { textAlign: 'right' })}>Deposit Paid</th>
            <th style={Object.assign({}, S.th, { textAlign: 'right' })}>Δ Dep.</th>
            <th style={Object.assign({}, S.th, { textAlign: 'right' })}>Remaining</th>
            <th style={S.th}>Est. Ship</th>
          </tr>
        </thead>
        <tbody>
          {outstanding.map(function(po) {
            // Full per-phase picture — reused from drilldown header logic
            // so the list scan and the drilldown details agree per row.
            var picture = poFinancialPicture(po, vendorPayments, items);
            var pic = poPaidPicture(po, vendorPayments, items, vendorCreditLedger);
            var total = pic.expected;
            var remaining = pic.remaining;
            var est = poEstShipDate(po, items);
            // The ship ESTIMATE has passed — a tracking fact, not a late
            // payment. Muted words, no red (the final is due when the
            // vendor's bill lands in the Inbox, not when the estimate passes).
            var shipLate = est && new Date(est) < new Date();
            var depRow = paymentsForPO(po, vendorPayments).find(function(x) { return x.payment_type === 'Deposit' || x.payment_type === 'Deposit Payment'; });
            // Δ color: near-zero gray, positive amber (vendor charged
            // more than expected at this phase), negative gray (deposit
            // partial — normal until final lands).
            var depGap = picture.gapDeposit;
            var depNearZero = Math.abs(depGap) < 0.005;
            var depColor = depNearZero ? '#888' : (depGap > 0 ? '#9A6B00' : '#888');
            var depLabel = depNearZero ? '\u2014' : (depGap > 0 ? '+' + fmtMoney(depGap) : fmtMoney(depGap));
            return (
              <tr key={po.id} style={S.row(true)}
                onClick={function() { p.onOpenPO(po.id); }}
                onMouseEnter={function(e) { e.currentTarget.style.background = '#FAFAF8'; }}
                onMouseLeave={function(e) { e.currentTarget.style.background = '#FFFFFF'; }}>
                <td style={Object.assign({}, S.td, { fontFamily: 'monospace', fontWeight: 500, color: '#111' })}>{po.po_ref || '—'}</td>
                <td style={S.td}>{po.vendorName || '—'}</td>
                <td style={Object.assign({}, S.td, { textAlign: 'right', color: '#444', fontVariantNumeric: 'tabular-nums' })}>{fmtMoney(total)}</td>
                <td style={Object.assign({}, S.td, { textAlign: 'right', color: '#888', fontVariantNumeric: 'tabular-nums' })}>{fmtMoney(picture.expectedDeposit)}</td>
                <td style={Object.assign({}, S.td, { textAlign: 'right' })}>
                  <div style={{ fontWeight: 500, color: '#111', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(picture.paidDeposit)}</div>
                  {depRow && <div style={{ fontSize: 10, color: '#888' }}>{fmtDate(depRow.payment_date)}</div>}
                </td>
                <td style={Object.assign({}, S.td, { textAlign: 'right', color: depColor, fontVariantNumeric: 'tabular-nums', fontWeight: depNearZero ? 400 : 600 })}>{depLabel}</td>
                <td style={Object.assign({}, S.td, { textAlign: 'right', fontWeight: 500, color: '#111', fontVariantNumeric: 'tabular-nums' })}>{fmtMoney(remaining)}</td>
                <td style={S.td}>
                  {est ? (
                    <span>
                      <span style={S.dot(shipLate ? '#888' : '#1A5C38')} />
                      {fmtDate(est)}
                      {shipLate && <span style={{ marginLeft: 6, fontSize: 10, color: '#888' }} title="The estimated ship date (order date + lead time) has passed. Says nothing about the vendor payment.">ship est. passed</span>}
                    </span>
                  ) : <span style={{ color: '#888' }}>—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── HISTORY VIEW ──────────────────────────────────────────────────────────────
function HistoryView(p) {
  var ctx = useAppContext();
  var vendorPayments = ctx.vendorPayments || [];
  var purchaseOrders = ctx.purchaseOrders || [];
  var setVendorPayments = ctx.setVendorPayments;

  var ss = useState('');               var search = ss[0];        var setSearch = ss[1];

  var rows = useMemo(function() {
    var sorted = vendorPayments.slice().sort(function(a, b) {
      return String(b.payment_date || '').localeCompare(String(a.payment_date || ''));
    });
    if (!search.trim()) return sorted;
    var q = search.trim().toLowerCase();
    return sorted.filter(function(p) {
      return [p.vendor_name, p.project_label, p.reference, p.payment_type, p.notes]
        .some(function(f) { return String(f || '').toLowerCase().indexOf(q) >= 0; });
    });
  }, [vendorPayments, search]);

  if (vendorPayments.length === 0) return (
    <div style={S.card}>
      <div style={S.emptyState}>No payments recorded yet.</div>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <input
          placeholder="Search vendor, project, reference..." autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
          value={search}
          onChange={function(e) { setSearch(e.target.value); }}
          style={{ width: '100%', maxWidth: 380, padding: '8px 12px', fontSize: 12, fontFamily: 'inherit',
            border: '0.5px solid #E0E0DC', borderRadius: 18, outline: 'none', background: '#FFFFFF' }} />
      </div>
      <div style={S.card}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={S.th}>Date</th>
              <th style={S.th}>Vendor</th>
              <th style={S.th}>Project</th>
              <th style={S.th}>Type</th>
              <th style={Object.assign({}, S.th, { textAlign: 'right' })}>Amount</th>
              <th style={S.th}>Reference</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(function(pmt) {
              var poRef = String(pmt.reference || '').split('·')[0].trim();
              var po = purchaseOrders.find(function(x) { return x.po_ref === poRef; });
              var clickable = !!po;
              return (
                <tr key={pmt.id} style={S.row(clickable)}
                  onClick={clickable ? function() { p.onOpenPO(po.id); } : undefined}
                  onMouseEnter={clickable ? function(e) { e.currentTarget.style.background = '#FAFAF8'; } : undefined}
                  onMouseLeave={clickable ? function(e) { e.currentTarget.style.background = '#FFFFFF'; } : undefined}>
                  <td style={S.td}>{fmtDate(pmt.payment_date)}</td>
                  <td style={Object.assign({}, S.td, { fontWeight: 500, color: '#111' })}>{pmt.vendor_name}</td>
                  <td style={S.td}>{pmt.project_label || '—'}</td>
                  <td style={S.td}>{pmt.payment_type}</td>
                  <td style={Object.assign({}, S.td, { textAlign: 'right', fontWeight: 500, color: '#111' })}>{fmtMoneyFull(pmt.amount)}</td>
                  <td style={Object.assign({}, S.td, { fontSize: 11, color: '#666', whiteSpace: 'normal', maxWidth: 280 })}>{formatVendorPaymentReference(pmt.reference) || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── PO DRILL-DOWN ─────────────────────────────────────────────────────────────
function PODrilldown(p) {
  var ctx = useAppContext();
  var items = ctx.items || [];
  var vendorPayments = ctx.vendorPayments || [];
  var setVendorPayments = ctx.setVendorPayments;
  var receipts = ctx.receipts || [];
  var vendorCreditLedger = ctx.vendorCreditLedger || [];
  var vendors = ctx.vendors || [];
  var isOwner = !!ctx.isOwner;

  var cis = useState(null);     var cancelItem = cis[0];   var setCancelItem = cis[1];
  var mas = useState(false);    var markPaidOpen = mas[0]; var setMarkPaidOpen = mas[1];
  var mns = useState('');       var adjNote = mns[0];      var setAdjNote = mns[1];
  var mes = useState('');       var adjErr = mes[0];       var setAdjErr = mes[1];
  var mcs = useState(false);    var adjSaving = mcs[0];    var setAdjSaving = mcs[1];
  // Session-scoped dismissals of orphan-payment banners. Reappears on
  // reload — sufficient for v1, since orphans are rare and the owner can
  // re-dismiss. A persistent dismissed_at column is a follow-up if it
  // becomes noisy.
  var dis = useState({});       var dismissedOrphans = dis[0];  var setDismissedOrphans = dis[1];

  var po = p.po;
  var poItemIds = Array.isArray(po.items) ? po.items : [];
  var poItems = items.filter(function(it) { return poItemIds.indexOf(it.id) >= 0; });
  var vendorCredit = calcVendorCreditBalance(vendorCreditLedger, po.vendorName);
  var payments = paymentsForPO(po, vendorPayments).slice().sort(function(a, b) {
    return String(a.payment_date || '').localeCompare(String(b.payment_date || ''));
  });
  var attachedReceipts = receipts.filter(function(r) { return r.poId === po.id; });
  // Paid / total read the canonical isPoFullyPaid picture (2026-09-20):
  // every payment_type by po_id, refunds subtracted, cancelled items out
  // of the total. The Mark-Paid-in-Full gap row and the status pill
  // below reconcile against these same two numbers.
  var paidPic = poPaidPicture(po, vendorPayments, items, vendorCreditLedger);
  var paid = paidPic.paid;
  var total = paidPic.expected;
  // Per-phase Expected vs Paid breakdown — drives the financial table
  // in the drilldown header. See poFinancialPicture above.
  var picture = poFinancialPicture(po, vendorPayments, items);
  var est = poEstShipDate(po, items);
  // Reconcile principal against PO total with the tolerance band. Drives
  // both the colored status pill and the Remaining/Overpaid stat below.
  var recon = poPaidReconciliation(paid, total);
  var remaining = recon.state === 'partial' ? recon.delta
                : recon.state === 'unpaid'  ? recon.delta
                : 0;
  var shipLate = est && new Date(est) < new Date();
  var status = vendorPaymentStatus(po, vendorPayments);
  var statusColor =
        recon.state === 'paid'     ? '#1A5C38'
      : recon.state === 'overpaid' ? '#1A5C38'
      : status === 'final-paid'    ? '#1A5C38'
      : status === 'deposit-paid'  ? '#F5C800'
      : '#888';
  var statusLabel =
        recon.state === 'paid'     ? 'Paid in full'
      : recon.state === 'overpaid' ? 'Paid in full \u00b7 overpaid by ' + fmtMoney(recon.delta)
      : recon.state === 'partial'  ? 'Partially paid \u00b7 ' + fmtMoney(recon.delta) + ' remaining'
      : status === 'deposit-paid'  ? 'Deposit paid'
      : 'Awaiting payment';

  // Orphan detection — pure logic in utils.findOrphanReceiptPayments.
  // Banner-dismissal state stays local (session-scoped).
  var orphanPayments = findOrphanReceiptPayments(payments, ctx.receipts || [])
    .filter(function(pmt) { return !dismissedOrphans[pmt.id]; });

  async function deleteOrphanPayment(pmt) {
    if (!isOwner) return;
    if (!window.confirm('Delete this Vendor Payment row? This cannot be undone.')) return;
    var res = await supabase.from('vendor_payments').delete().eq('id', pmt.id);
    if (res.error) {
      console.error('Delete orphan payment failed:', res.error);
      window.alert('Delete failed: ' + (res.error.message || 'unknown error'));
      return;
    }
    if (setVendorPayments) {
      setVendorPayments(function(prev) { return prev.filter(function(x) { return x.id !== pmt.id; }); });
    }
  }

  function dismissOrphan(pmt) {
    setDismissedOrphans(function(prev) {
      var next = Object.assign({}, prev);
      next[pmt.id] = true;
      return next;
    });
  }

  // Owner-only Mark Paid in Full handler. Row shape comes from
  // utils.buildOwnerAdjustmentRow — pure, testable.
  async function commitMarkPaidInFull() {
    var row = buildOwnerAdjustmentRow(po, paid, total, adjNote);
    if (!row) {
      var trimmed = String(adjNote || '').trim();
      if (!trimmed) { setAdjErr('Please add a note explaining the reason.'); return; }
      setAdjErr('No gap to close — PO is already at or beyond paid-in-full.');
      return;
    }
    setAdjSaving(true);
    setAdjErr('');
    row = Object.assign({}, row, { created_at: new Date().toISOString() });
    var res = await supabase.from('vendor_payments').insert(row).select().single();
    if (res.error) {
      console.error('Mark Paid in Full insert failed:', res.error);
      setAdjErr(res.error.message || 'Insert failed.');
      setAdjSaving(false);
      return;
    }
    if (setVendorPayments) {
      setVendorPayments(function(prev) { return [res.data].concat(prev); });
    }
    setAdjSaving(false);
    setMarkPaidOpen(false);
    setAdjNote('');
  }

  return (
    <div style={S.wrap}>

      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <button style={S.ghostBtn} onClick={p.onBack}>← Back</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 22, fontWeight: 600, color: '#111', fontFamily: 'monospace' }}>{po.po_ref || '—'}</div>
        <div style={{ fontSize: 14, color: '#444' }}>{po.vendorName || '—'}</div>
        <div style={{ fontSize: 12, color: '#888' }}>· {po.project || '—'}</div>
        <div style={{ flex: 1 }} />
        {/* Owner-only adjustment button: synthetic 'Adjustment' row brings
            the principal sum up to po.totalNet. Visible only when the
            reconciliation says we're short of paid-in-full. */}
        {isOwner && (recon.state === 'partial' || recon.state === 'unpaid') && (
          <button
            data-testid="mark-paid-in-full-btn"
            onClick={function() { setMarkPaidOpen(true); setAdjNote(''); setAdjErr(''); }}
            style={Object.assign({}, S.ghostBtn, { height: 26, fontSize: 11, marginRight: 12 })}
            title="Owner-only: closes the gap to PO total with a notated Adjustment row.">
            Mark PO as Paid in Full
          </button>
        )}
        <span style={{ fontSize: 11, color: '#444', display: 'inline-flex', alignItems: 'center' }}>
          <span style={S.dot(statusColor)} />{statusLabel}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 24, marginBottom: 14, flexWrap: 'wrap' }}>
        <Stat label="PO Total"  value={fmtMoney(total)} />
        <Stat label="Paid"      value={fmtMoney(paid)} />
        <Stat label="Remaining" value={fmtMoney(remaining)} red={remaining > 0 && status === 'deposit-paid'} />
        {est && <Stat label={shipLate ? 'Est. ship · passed' : 'Est. ship'} value={fmtDate(est)} />}
        {vendorCredit !== 0 && <Stat label={po.vendorName + ' credit'} value={fmtMoney(vendorCredit)} />}
      </div>

      {/* ── Expected vs Paid breakdown ─────────────────────────────────
          Per-phase financial picture. Surfaces gaps between what the PO
          expected (per-item at-cost: vdp×product + timed fees) and what
          was actually paid (sum of vendor_payments by phase, 'Fee' rows
          on their own line). The total row reconciles to PO Total vs the
          canonical paid figure. */}
      <div style={{ marginBottom: 22, maxWidth: 560 }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888', marginBottom: 6 }}>
          Expected vs Paid
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '0.5px solid #E0E0DC' }}>
              <th style={{ padding: '6px 8px', textAlign: 'left',  color: '#888', fontWeight: 500, width: 90 }}></th>
              <th style={{ padding: '6px 8px', textAlign: 'right', color: '#888', fontWeight: 500 }}>Expected</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', color: '#888', fontWeight: 500 }}>Paid</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', color: '#888', fontWeight: 500, width: 100 }}>Δ</th>
            </tr>
          </thead>
          <tbody>
            {[
              { label: 'Deposit', expected: picture.expectedDeposit, paid: picture.paidDeposit, gap: picture.gapDeposit, bold: false },
              { label: 'Final',   expected: picture.expectedFinal,   paid: picture.paidFinal,   gap: picture.gapFinal,   bold: false },
              // Fee rows (freight / crating / tariff billed separately)
              // have no phase of their own — their expected share is
              // already inside Deposit/Final. Paid only; no Δ.
              { label: 'Fees',    expected: null,                    paid: picture.paidFees,    gap: 0,                  bold: false, hide: Math.abs(picture.paidFees) < 0.005 },
              { label: 'Total',   expected: picture.poTotal,         paid: picture.paidTotal,   gap: picture.gapTotal,   bold: true  },
            ].filter(function(row) { return !row.hide; }).map(function(row) {
              var nearZero = row.expected == null || Math.abs(row.gap) < 0.005;
              var gapColor = nearZero ? '#888' : (row.gap > 0 ? '#9A6B00' : '#C8322A');
              var gapLabel = nearZero ? '\u2014' : (row.gap > 0 ? '+' + fmtMoney(row.gap) : fmtMoney(row.gap));
              return (
                <tr key={row.label} style={row.bold ? { borderTop: '0.5px solid #E0E0DC', background: '#FAFAF8' } : {}}>
                  <td style={{ padding: '6px 8px', color: '#444', fontWeight: row.bold ? 600 : 400 }}>{row.label}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: '#444', fontVariantNumeric: 'tabular-nums', fontWeight: row.bold ? 600 : 400 }}>{row.expected == null ? "\u2014" : fmtMoney(row.expected)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: '#444', fontVariantNumeric: 'tabular-nums', fontWeight: row.bold ? 600 : 400 }}>{fmtMoney(row.paid)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: gapColor, fontVariantNumeric: 'tabular-nums', fontWeight: row.bold ? 600 : 400 }}>{gapLabel}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 18, alignItems: 'start' }}>

        {/* Items */}
        <div style={S.card}>
          <div style={{ padding: '12px 16px', borderBottom: '0.5px solid #E0E0DC', background: '#FAFAF8' }}>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888' }}>Items on this PO ({poItems.length})</div>
          </div>
          {poItems.length === 0 ? (
            <div style={S.emptyState}>No items linked to this PO.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {poItems.map(function(it) {
                  var cancelled = it.status === 'Cancelled';
                  return (
                    <tr key={it.id} style={Object.assign({}, S.row(false), cancelled ? { opacity: 0.5 } : {})}>
                      <td style={Object.assign({}, S.td, { fontFamily: 'monospace', fontSize: 11, color: '#888', width: 120 })}>{it.itemNumber || '—'}</td>
                      <td style={Object.assign({}, S.td, { fontWeight: 500, color: '#111', whiteSpace: 'normal', textDecoration: cancelled ? 'line-through' : 'none' })}>
                        {it.item || it.productName || '—'}
                        {cancelled && <span style={{ marginLeft: 8, fontSize: 10, color: '#888', textDecoration: 'none' }}>(Cancelled)</span>}
                      </td>
                      <td style={Object.assign({}, S.td, { textAlign: 'right', color: '#444' })}>{fmtMoney(parseFloat(it.netPrice) * (parseInt(it.qty) || 1))}</td>
                      <td style={Object.assign({}, S.td, { textAlign: 'right', width: 100 })}>
                        {!cancelled && (
                          <button onClick={function() { setCancelItem(it); }}
                            style={{ height: 24, padding: '0 10px', borderRadius: 14, border: '0.5px solid #C8322A', background: '#FFFFFF', color: '#C8322A', fontSize: 10, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}>
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Vendor Payments */}
        <div>
          {/* Orphan-receipt banner: payment rows whose source receipt was
              deleted or unapproved. Owner can either delete the row or
              dismiss the banner (session-scoped). Banner is rendered per
              orphan so the bookkeeper sees exactly which payment is
              under review. */}
          {orphanPayments.length > 0 && (
            <div style={{ marginBottom: 14 }} data-testid="orphan-payment-banner">
              {orphanPayments.map(function(pmt) {
                return (
                  <div key={pmt.id} style={{ padding: '10px 14px', marginBottom: 8, background: '#FEF7E7', border: '0.5px solid #F5C800', borderRadius: 6 }}>
                    <div style={{ fontSize: 12, color: '#7A6500', fontWeight: 500, marginBottom: 4 }}>
                      Source receipt was deleted or unapproved.
                    </div>
                    <div style={{ fontSize: 11, color: '#7A6500', marginBottom: 8 }}>
                      Payment row: {pmt.payment_type} · {fmtMoneyFull(pmt.amount)} · {fmtDate(pmt.payment_date)}. Review and decide whether to keep this record or delete it.
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={function() { dismissOrphan(pmt); }}
                        style={Object.assign({}, S.ghostBtn, { height: 24, fontSize: 10 })}>
                        Keep payment
                      </button>
                      {isOwner && (
                        <button
                          onClick={function() { deleteOrphanPayment(pmt); }}
                          style={{ height: 24, padding: '0 12px', borderRadius: 14, border: '0.5px solid #C8322A', background: '#FFFFFF', color: '#C8322A', fontSize: 10, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}>
                          Delete payment row
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={Object.assign({}, S.card, { marginBottom: 14 })}>
            <div style={{ padding: '12px 16px', borderBottom: '0.5px solid #E0E0DC', background: '#FAFAF8', display: 'flex', alignItems: 'center' }}>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888', flex: 1 }}>Vendor Payments</div>
              <button style={Object.assign({}, S.ghostBtn, { height: 26, fontSize: 11 })} onClick={p.onRecord} title="For payments without a receipt — approving a vendor receipt already creates this row.">+ Record manually</button>
            </div>
            {payments.length === 0 ? (
              <div style={{ padding: '24px 18px', fontSize: 12, color: '#888', fontStyle: 'italic', textAlign: 'center' }}>No payments yet.</div>
            ) : (
              <div style={{ padding: '6px 0' }}>
                {payments.map(function(pmt) {
                  // Provenance label: auto-created rows (created_via='receipt_cascade')
                  // show a "From receipt …" line linking back to the source.
                  // Manual rows show no provenance line.
                  var srcReceipt = pmt.source_receipt_id
                    ? (ctx.receipts || []).find(function(r) { return r.id === pmt.source_receipt_id; })
                    : null;
                  var srcLabel = srcReceipt
                    ? 'From receipt ' + (srcReceipt.filename || srcReceipt.vendorName || srcReceipt.id)
                    : (pmt.created_via === 'receipt_cascade' && pmt.source_receipt_id
                        ? 'From receipt (deleted)'
                        : null);
                  return (
                    <div key={pmt.id} style={{ padding: '10px 16px', borderBottom: '0.5px solid #EFEFEC' }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <span style={{ fontWeight: 500, color: '#111' }}>{pmt.payment_type}</span>
                        {pmt.payment_method && (
                          <span style={{ fontSize: 10, color: '#888' }}>· {pmt.payment_method}</span>
                        )}
                        <span style={{ flex: 1 }} />
                        <span style={{ fontWeight: 500, color: '#111' }}>{fmtMoneyFull(pmt.amount)}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{fmtDate(pmt.payment_date)}{pmt.reference ? ' · ' + String(pmt.reference).split('·')[0].trim() : ''}</div>
                      {srcLabel && (
                        <div style={{ fontSize: 10, color: '#888', marginTop: 2, fontStyle: 'italic' }}>{srcLabel}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cancel Item modal */}
          {cancelItem && <CancelItemModal item={cancelItem} onClose={function() { setCancelItem(null); }} onCancelled={function() { setCancelItem(null); }} />}

          {/* Receipts */}
          {attachedReceipts.length > 0 && (
            <div style={S.card}>
              <div style={{ padding: '12px 16px', borderBottom: '0.5px solid #E0E0DC', background: '#FAFAF8' }}>
                <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888' }}>Receipts ({attachedReceipts.length})</div>
              </div>
              <div style={{ padding: '6px 0' }}>
                {attachedReceipts.map(function(r) {
                  return (
                    <div key={r.id} style={{ padding: '10px 16px', borderBottom: '0.5px solid #EFEFEC' }}>
                      <div style={{ fontSize: 12, color: '#111', fontWeight: 500 }}>{r.filename || r.vendorName || 'Receipt'}</div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{fmtDate(r.receiptDate)} · {fmtMoneyFull(r.totalAmount)} · {r.status}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Owner-only Mark Paid in Full modal. Captures a required note and
          writes a synthetic 'Adjustment' vendor_payments row closing the
          gap to po.totalNet. The Adjustment payment_type is in the
          principal sum filter, so the next render of the reconciliation
          lands in the 'paid' state. */}
      <ConfirmationModal
        open={markPaidOpen}
        heading="Mark PO as Paid in Full?"
        body={'This writes an Adjustment row for ' + fmtMoney(Math.max(total - paid, 0)) + ' that closes the gap to PO total. Owner-only. The note is required and stored in the audit trail.'}
        confirmLabel="Mark Paid"
        cancelLabel="Cancel"
        tone="primary"
        confirming={adjSaving}
        error={adjErr}
        onCancel={function() {
          if (adjSaving) return;
          setMarkPaidOpen(false);
          setAdjNote('');
          setAdjErr('');
        }}
        onConfirm={commitMarkPaidInFull}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: '#444' }}>
            Reason / explanation <span style={{ color: '#C8322A' }}>*</span>
          </label>
          <textarea
            data-testid="mark-paid-note"
            value={adjNote}
            onChange={function(e) { setAdjNote(e.target.value); }}
            placeholder="e.g. Negotiated vendor discount of $200 — closing PO."
            rows={3}
            disabled={adjSaving}
            style={{ width: '100%', padding: '8px 10px', fontSize: 12, fontFamily: 'inherit', border: '0.5px solid #E0E0DC', borderRadius: 6, outline: 'none', resize: 'vertical' }} />
        </div>
      </ConfirmationModal>
    </div>
  );
}

function Stat(p) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888', marginBottom: 4 }}>{p.label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: p.red ? '#C8322A' : '#111' }}>{p.value}</div>
    </div>
  );
}

// ── RECORD PAYMENT MODAL ──────────────────────────────────────────────────────
export function RecordPaymentModal(props) {
  var ctx = useAppContext();
  var purchaseOrders = ctx.purchaseOrders || [];
  var items = ctx.items || [];
  var saveReceiptToDb = ctx.saveReceiptToDb;
  var setReceipts = ctx.setReceipts;
  var applyVendorReceiptSideEffects = ctx.applyVendorReceiptSideEffects;
  var userEmail = (ctx.userEmail || '').toLowerCase();
  var isOwner = !!ctx.isOwner;
  var firmSettings = ctx.firmSettings || {};

  var ps = useState(props.po ? props.po.id : '');           var poId = ps[0];           var setPoId = ps[1];
  var ts = useState('Deposit');                             var type = ts[0];           var setType = ts[1];
  var ams = useState(0);                                    var amount = ams[0];        var setAmount = ams[1];
  var ccs = useState(0);                                    var ccFee = ccs[0];         var setCcFee = ccs[1];
  var ds = useState(new Date().toISOString().slice(0,10));  var date = ds[0];           var setDate = ds[1];
  var rs = useState('');                                    var ref = rs[0];            var setRef = rs[1];
  var ns = useState('');                                    var notes = ns[0];          var setNotes = ns[1];
  var fs = useState(null);                                  var file = fs[0];           var setFile = fs[1];
  var ses = useState('');                                   var err = ses[0];           var setErr = ses[1];
  var sbs = useState(false);                                var saving = sbs[0];        var setSaving = sbs[1];

  // Phase 2B per-item allocation state. `allocations` is keyed by item_id;
  // each entry is { amount_share, cc_fee_share }. `userTouched` flips to
  // true when the user edits any per-item field OR clicks Even split,
  // and gates the allocation_method tag (net_weighted vs override) plus
  // the auto-redefault behavior on parent amount changes.
  var als = useState({});                                   var allocations = als[0];   var setAllocations = als[1];
  var uts = useState(false);                                var userTouched = uts[0];   var setUserTouched = uts[1];

  var fileInputRef = useRef(null);
  var po = purchaseOrders.find(function(x) { return x.id === poId; });

  // Resolve PO's items to full item objects (non-Cancelled only). Display
  // order follows po.items order so the user sees the same order they see
  // on the PO detail page.
  var eligibleItems = useMemo(function() {
    if (!po || !Array.isArray(po.items) || po.items.length === 0) return [];
    var out = [];
    po.items.forEach(function(id) {
      var found = items.find(function(x) { return x.id === id; });
      if (found && (found.status || '') !== 'Cancelled') out.push(found);
    });
    return out;
  }, [poId, po, items]);

  var showAllocationUI = eligibleItems.length >= 2;

  // Default allocation = net-weighted across eligibleItems. The cascade
  // tags 'cascade_net_weighted'; here we tag 'manual_net_weighted' since
  // it's the manual-entry path. Even-split helper tags 'manual_override'
  // by default (a deliberate user choice).
  function recomputeNetWeighted(amt, cc) {
    var children = allocateVendorPaymentToItems({
      amount: parseFloat(amt) || 0,
      ccFee:  parseFloat(cc)  || 0,
      items:  eligibleItems,
      allocationMethod: 'manual_net_weighted',
    });
    var map = {};
    children.forEach(function(c) {
      map[c.item_id] = { amount_share: c.amount_share, cc_fee_share: c.cc_fee_share };
    });
    return map;
  }
  function recomputeEvenSplit(amt, cc) {
    var children = allocateVendorPaymentEvenly({
      amount: parseFloat(amt) || 0,
      ccFee:  parseFloat(cc)  || 0,
      items:  eligibleItems,
      allocationMethod: 'manual_override',
    });
    var map = {};
    children.forEach(function(c) {
      map[c.item_id] = { amount_share: c.amount_share, cc_fee_share: c.cc_fee_share };
    });
    return map;
  }

  // Reset user-touched flag whenever the PO changes — a new PO has a
  // different item set, so any prior manual edits are stale.
  useEffect(function() {
    setUserTouched(false);
  }, [poId]);

  // Auto-default allocations when PO/amount/ccFee change AND user hasn't
  // touched any per-item field. Once userTouched=true, leave the user's
  // values alone; they can click Reset to default to come back.
  useEffect(function() {
    if (userTouched) return;
    if (!showAllocationUI) {
      setAllocations({});
      return;
    }
    setAllocations(recomputeNetWeighted(amount, ccFee));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId, amount, ccFee, eligibleItems.length, userTouched]);

  // Validation: SUM(children.amount_share) ≈ parent.amount AND
  //             SUM(children.cc_fee_share) ≈ parent.ccFee (within $0.01)
  // Only enforced when the allocation UI is shown (multi-item POs).
  var amountSum = Object.keys(allocations).reduce(function(s, k) {
    return s + (parseFloat(allocations[k].amount_share) || 0);
  }, 0);
  var ccFeeSum = Object.keys(allocations).reduce(function(s, k) {
    return s + (parseFloat(allocations[k].cc_fee_share) || 0);
  }, 0);
  var amountDiff = Math.round((amountSum - (parseFloat(amount) || 0)) * 100) / 100;
  var ccFeeDiff  = Math.round((ccFeeSum  - (parseFloat(ccFee)  || 0)) * 100) / 100;
  var allocationOk = !showAllocationUI || (Math.abs(amountDiff) < 0.01 && Math.abs(ccFeeDiff) < 0.01);

  function setAllocField(itemId, field, val) {
    var next = Object.assign({}, allocations);
    next[itemId] = Object.assign({ amount_share: 0, cc_fee_share: 0 }, next[itemId] || {});
    next[itemId][field] = val;
    setAllocations(next);
    setUserTouched(true);
  }
  function resetAllocationsToDefault() {
    setAllocations(recomputeNetWeighted(amount, ccFee));
    setUserTouched(false);
  }
  function applyEvenSplit() {
    setAllocations(recomputeEvenSplit(amount, ccFee));
    setUserTouched(true);
  }

  async function uploadReceiptFile(f, payment_id, kindFromType) {
    if (!f) return null;
    var rid = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : (Date.now() + '-' + Math.random()).toString();
    var ext = f.name.split('.').pop();
    var ym = new Date().toISOString().slice(0, 7);
    var path = kindFromType + '/' + ym + '/' + rid + '.' + ext;
    var up = await supabase.storage.from('receipts').upload(path, f, { contentType: f.type });
    if (up.error) {
      console.error('receipt upload failed:', up.error);
      return null;
    }
    var receipt = {
      id: rid,
      kind: kindFromType,
      status: 'approved',
      storagePath: path,
      filename: f.name,
      mimeType: f.type,
      vendorName: po ? po.vendorName : '',
      receiptDate: date,
      totalAmount: Number(amount) || 0,
      currency: 'USD',
      poId: poId,
      itemId: '',
      projectLabel: po ? po.project : '',
      createdBy: userEmail,
      reference: ref,
      notes: notes,
      approvedAt: new Date().toISOString(),
      approvedBy: userEmail,
    };
    var ok = await saveReceiptToDb(receipt);
    if (ok && setReceipts) setReceipts(function(prev) { return [receipt].concat(prev); });
    return receipt;
  }

  async function submit() {
    setErr('');
    if (!poId) { setErr('Pick a PO.'); return; }
    var amt = Number(amount) || 0;
    if (!amt || amt <= 0) { setErr('Enter a valid amount.'); return; }
    // Date paid is required (2026-06-14, Part A). State prefills today
    // at modal open (see useState line above), so this only fires when
    // the bookkeeper deliberately clears the field. Mirrors the
    // approve() gate in ReceiptsTab — every UI surface that writes a
    // vendor_payments row must surface a non-empty date BEFORE invoking
    // the cascade (utils.js buildVendorPaymentFromReceipt throws if
    // receiptDate is empty; this gate catches it with a friendly
    // message instead of letting the cascade explode).
    if (!date || String(date).trim() === '') {
      setErr('Enter the date the payment was made.');
      return;
    }
    if (showAllocationUI && !allocationOk) {
      setErr('Per-item shares don\u2019t add up. Adjust values or click "Reset to default".');
      return;
    }
    setSaving(true);

    var kindFromType = type === 'Deposit' ? 'vendor_deposit'
                     : type === 'Final'   ? 'vendor_final'
                     : type === 'Full'    ? 'vendor_full_payment'
                     : 'vendor_fee';

    // Upload receipt first if attached, then run cascade off the receipt.
    // No file = synthesize a minimal receipt object so the cascade still runs.
    var receipt = null;
    if (file) {
      receipt = await uploadReceiptFile(file, null, kindFromType);
    }
    if (!receipt) {
      // Synthetic receipt: enough for the cascade (kind, poId, amount, date,
      // vendor, project). Manual-no-file entries do NOT have a DB-backed
      // receipts row, so they carry no `id`. `_manual: true` lets
      // shouldApplyVendorReceiptCascade proceed despite the missing id;
      // buildVendorPaymentFromReceipt sees the absent id and writes
      // source_receipt_id=NULL + created_via='manual'.
      //
      // Pre-2026-06-01 this carried `id: 'manual-' + Date.now()` which
      // tried to flow into the uuid-typed source_receipt_id column and
      // failed with `invalid input syntax for type uuid` once the
      // 2026-05-12 receipt-integration columns landed in prod.
      receipt = {
        _manual: true,
        kind: kindFromType,
        status: 'approved',
        poId: poId,
        vendorName: po ? po.vendorName : '',
        receiptDate: date,
        totalAmount: amt,
        projectLabel: po ? po.project : '',
        notes: notes,
      };
    }
    // Pass the user-entered CC fee (default 0) into the cascade so it
    // lands on the vendor_payments row alongside the principal amount.
    //
    // Cascade failures surface via window.alert with a friendly tone
    // (post-2026-05-29 incident + post-2026-05-30 alert pass). On
    // failure the Record Payment modal stays open so the bookkeeper
    // can adjust + retry.
    function greeting() {
      if (isOwner) return 'Hi Cimon';
      var n = displayNameFor(userEmail, firmSettings);
      return n ? 'Hi ' + n : 'Hey there';
    }

    var ccAmt = Number(ccFee) || 0;

    // Phase 2B: pass per-item allocations into the cascade when the
    // multi-item UI is in play. Single-item POs and no-PO entries go
    // through the cascade's default path (allocateVendorPaymentToItems
    // builds children server-side; or none when no eligible items).
    var manualOpts = { ccFee: ccAmt };
    if (showAllocationUI) {
      manualOpts.itemAllocations = eligibleItems.map(function(it) {
        var a = allocations[it.id] || { amount_share: 0, cc_fee_share: 0 };
        return {
          item_id:      it.id,
          amount_share: parseFloat(a.amount_share) || 0,
          cc_fee_share: parseFloat(a.cc_fee_share) || 0,
        };
      });
      manualOpts.allocationMethod = userTouched ? 'manual_override' : 'manual_net_weighted';
    }

    if (applyVendorReceiptSideEffects) {
      var res;
      try {
        res = await applyVendorReceiptSideEffects(receipt, manualOpts);
      } catch (e) {
        console.error('vendor receipt cascade threw:', e);
        window.alert(
          greeting() + ' \u2014 something unexpected happened while recording this payment. The payment didn\u2019t go through. Send Cimon a screenshot of the error below and try again after she\u2019s checked in.\n\n' +
          'Technical detail: ' + (e && e.message)
        );
        setSaving(false);
        return;
      }

      if (res && res.ok === false) {
        if (res.failure_at === 'vendor_mismatch') {
          window.alert(
            greeting() + ' \u2014 the vendor on this PO ("' + (res.vendorName || '?') + '") doesn\u2019t match any vendor in our directory.\n\n' +
            'Open the linked PO in Procurement \u2192 Purchase Orders and check the vendor field, or update the vendor in Directory \u2192 Vendors to add an alias for "' + (res.vendorName || '?') + '".\n\n' +
            'Once the vendor resolves, submit this form again.'
          );
        } else if (res.failure_at === 'cc_fee_insert') {
          window.alert(
            greeting() + ' \u2014 the main payment was recorded, but the credit card fee couldn\u2019t be saved. The vendor payment is in the system; only the CC fee piece is missing.\n\n' +
            'Submit the form again to retry just the CC fee \u2014 the payment won\u2019t be duplicated.\n\n' +
            'Technical detail: ' + (res.reason || 'unknown')
          );
        } else {
          window.alert(
            greeting() + ' \u2014 something\u2019s not right on the database side that Cimon needs to look at. Re-submitting won\u2019t fix it yet. Send Cimon a screenshot of this form and the error below so she can sort it out.\n\n' +
            'Technical detail: ' + (res.reason || 'unknown')
          );
        }
        setSaving(false);
        return;  // hold the form open
      }

      if (res && res.ok && res.failure_at === 'item_updates') {
        window.alert(
          greeting() + ' \u2014 the payment is recorded, but we couldn\u2019t update the order-placed date on ' + res.itemsFailed + ' item(s) on this PO.\n\n' +
          'You\u2019ll need to set the order date manually on those items from the Tracker or item detail. Then advance their status if needed.\n\n' +
          'This won\u2019t fix itself \u2014 handle the items individually.'
        );
        // Fall through to close — payment side committed.
      }
    }
    setSaving(false);
    props.onClose();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 520, maxWidth: '92vw', background: '#fff', borderRadius: 10, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 16 }}>Record Vendor Payment</div>

        <Field label="PO">
          <select value={poId} onChange={function(e) { setPoId(e.target.value); }}
            style={{ width: '100%', padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', border: '0.5px solid #E0E0DC', borderRadius: 6, background: '#fff', outline: 'none' }}>
            <option value="">— Select PO —</option>
            {purchaseOrders.map(function(po) {
              return <option key={po.id} value={po.id}>{(po.po_ref || 'PO') + ' · ' + (po.vendorName || '') + ' · ' + (po.project || '')}</option>;
            })}
          </select>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Type">
            <select value={type} onChange={function(e) { setType(e.target.value); }}
              style={{ width: '100%', padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', border: '0.5px solid #E0E0DC', borderRadius: 6, background: '#fff', outline: 'none' }}>
              <option>Deposit</option>
              <option>Final</option>
              <option>Full</option>
              <option>Fee</option>
            </select>
          </Field>
          <Field label="Date">
            <input type="date" value={date} onChange={function(e) { setDate(e.target.value); }}
              style={{ width: '100%', padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', border: '0.5px solid #E0E0DC', borderRadius: 6, background: '#fff', outline: 'none' }} />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Amount">
            <CurrencyInput data-testid="record-payment-amount" value={amount} onValueChange={function(n) { setAmount(n); }}
              style={{ width: '100%', padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', border: '0.5px solid #E0E0DC', borderRadius: 6, background: '#fff', outline: 'none' }} />
          </Field>
          <Field label="CC fee (if paid by card)">
            <CurrencyInput data-testid="record-payment-cc-fee" value={ccFee} onValueChange={function(n) { setCcFee(n); }}
              title="The credit-card processing fee the bank charged you on this payment. Rolls up onto the client's final invoice."
              style={{ width: '100%', padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', border: '0.5px solid #E0E0DC', borderRadius: 6, background: '#fff', outline: 'none' }} />
          </Field>
        </div>

        {showAllocationUI && (
          <div data-testid="record-payment-allocation-ui" style={{ marginBottom: 12, padding: 12, border: '0.5px solid #E0E0DC', borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#111' }}>
                Per-item allocation ({eligibleItems.length} items)
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" onClick={resetAllocationsToDefault} data-testid="alloc-reset-default"
                  style={Object.assign({}, S.ghostBtn, { fontSize: 10, padding: '4px 10px' })}>
                  Reset to default
                </button>
                <button type="button" onClick={applyEvenSplit} data-testid="alloc-even-split"
                  style={Object.assign({}, S.ghostBtn, { fontSize: 10, padding: '4px 10px' })}>
                  Even split
                </button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: ccFee > 0 ? '2fr 1fr 1fr 1fr' : '2fr 1fr 1fr', gap: 8, fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              <div>Item</div>
              <div style={{ textAlign: 'right' }}>Net price</div>
              <div style={{ textAlign: 'right' }}>Share of payment</div>
              {ccFee > 0 && <div style={{ textAlign: 'right' }}>Share of CC fee</div>}
            </div>
            {eligibleItems.map(function(it) {
              var a = allocations[it.id] || { amount_share: 0, cc_fee_share: 0 };
              var label = it.item || it.product_name || ('item ' + String(it.id).slice(0, 8));
              return (
                <div key={it.id} data-testid={"alloc-row-" + it.id} style={{ display: 'grid', gridTemplateColumns: ccFee > 0 ? '2fr 1fr 1fr 1fr' : '2fr 1fr 1fr', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ fontSize: 12, color: '#111' }}>{label}</div>
                  <div style={{ fontSize: 12, color: '#666', textAlign: 'right' }}>{fmtMoney(parseFloat(it.netPrice) || 0)}</div>
                  <CurrencyInput data-testid={"alloc-amount-" + it.id} value={a.amount_share}
                    onValueChange={function(n) { setAllocField(it.id, 'amount_share', n); }}
                    style={{ width: '100%', padding: '6px 10px', fontSize: 12, fontFamily: 'inherit', border: '0.5px solid #E0E0DC', borderRadius: 6, background: '#fff', outline: 'none', textAlign: 'right' }} />
                  {ccFee > 0 && (
                    <CurrencyInput data-testid={"alloc-ccfee-" + it.id} value={a.cc_fee_share}
                      onValueChange={function(n) { setAllocField(it.id, 'cc_fee_share', n); }}
                      style={{ width: '100%', padding: '6px 10px', fontSize: 12, fontFamily: 'inherit', border: '0.5px solid #E0E0DC', borderRadius: 6, background: '#fff', outline: 'none', textAlign: 'right' }} />
                  )}
                </div>
              );
            })}
            <div data-testid="alloc-totals" style={{ marginTop: 8, paddingTop: 8, borderTop: '0.5px solid #E0E0DC', display: 'grid', gridTemplateColumns: ccFee > 0 ? '2fr 1fr 1fr 1fr' : '2fr 1fr 1fr', gap: 8, fontSize: 11 }}>
              <div style={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10 }}>Allocated</div>
              <div></div>
              <div data-testid="alloc-amount-sum" style={{ textAlign: 'right', color: Math.abs(amountDiff) < 0.01 ? '#1A5C38' : '#C8322A', fontWeight: 600 }}>
                {fmtMoney(amountSum)} / {fmtMoney(parseFloat(amount) || 0)}
              </div>
              {ccFee > 0 && (
                <div data-testid="alloc-ccfee-sum" style={{ textAlign: 'right', color: Math.abs(ccFeeDiff) < 0.01 ? '#1A5C38' : '#C8322A', fontWeight: 600 }}>
                  {fmtMoney(ccFeeSum)} / {fmtMoney(parseFloat(ccFee) || 0)}
                </div>
              )}
            </div>
            {!allocationOk && (
              <div data-testid="alloc-validation-error" style={{ marginTop: 8, fontSize: 11, color: '#C8322A' }}>
                {Math.abs(amountDiff) >= 0.01 && <div>Amount shares are off by {fmtMoney(amountDiff)}.</div>}
                {ccFee > 0 && Math.abs(ccFeeDiff) >= 0.01 && <div>CC fee shares are off by {fmtMoney(ccFeeDiff)}.</div>}
              </div>
            )}
          </div>
        )}

        <Field label="Reference">
          <input placeholder="wire #, check #, etc." value={ref} onChange={function(e) { setRef(e.target.value); }}
            style={{ width: '100%', padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', border: '0.5px solid #E0E0DC', borderRadius: 6, background: '#fff', outline: 'none' }} />
        </Field>

        <Field label="Notes">
          <textarea value={notes} onChange={function(e) { setNotes(e.target.value); }} rows={2}
            style={{ width: '100%', padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', border: '0.5px solid #E0E0DC', borderRadius: 6, background: '#fff', outline: 'none', resize: 'vertical' }} />
        </Field>

        <Field label="Receipt (optional)">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf"
              onChange={function(e) { setFile(e.target.files && e.target.files[0]); }}
              style={{ display: 'none' }} id="record-payment-file-input" />
            <label htmlFor="record-payment-file-input" style={Object.assign({}, S.ghostBtn, { display: 'inline-flex', alignItems: 'center', height: 30 })}>
              {file ? 'Change file' : 'Attach receipt'}
            </label>
            {file && <span style={{ fontSize: 12, color: '#444' }}>{file.name}</span>}
          </div>
        </Field>

        {err && <div style={{ marginBottom: 12, padding: '8px 12px', background: '#FDECEA', border: '0.5px solid #F2A39C', color: '#C8322A', fontSize: 11, borderRadius: 6 }}>{err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <button style={S.ghostBtn} disabled={saving} onClick={props.onClose}>Cancel</button>
          <button data-testid="record-payment-save" style={S.primaryBtn} disabled={saving || !allocationOk} onClick={submit}>
            {saving ? 'Saving…' : 'Save Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field(p) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888', marginBottom: 4 }}>{p.label}</div>
      {p.children}
    </div>
  );
}
